import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

import { QuickTurnPartMaterial, QuickTurnSalesOrder } from '@partsbadger/types';
import { get, patch, post, remove, API_ROOT, getAuthToken } from '@partsbadger/utils';
import axios from 'axios';
import UserStore from './UserStore';

const checkIsValidUUID = (id: string) => {
    const regex = new RegExp(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
    return regex.test(id);
};

function getHeader(has_files = false) {
    const token = getAuthToken();

    if (token) {
        return {
            'Content-Type': has_files ? 'multipart/form-data' : 'application/json',
            Authorization: `Token ${token}`,
        };
    } else {
        return {
            'Content-Type': has_files ? 'multipart/form-data' : 'application/json',
        };
    }
}

function degreesToRadians(degrees: number) {
    return degrees * (Math.PI / 180);
}

interface IEmailSignUp {
    email_address: string;
    source: string;
}

interface ICompanyParams {
    name: string;
    value: {
        enabled?: boolean;
    };
}

class QuickTurnPartStore {
    loading = false;

    materials?: QuickTurnPartMaterial[];

    name?: string;
    outerDiameter = 0;
    overallLength = 0;
    hasThruHole = false;
    innerDiameter = 0;
    hasFrontCountersink = false;
    frontCountersinkAngle = 0;
    frontCountersinkDepth = 0;
    hasFlange = false;
    flangeDiameter = 0;
    flangeThickness = 0;
    hasFrontCounterbore = true;
    frontCounterboreDiameter = 0;
    frontCounterboreDepth = 0;
    chamferWidth = 0;
    toolNoseRad = 0;
    enableSectionalView = false;
    material: string | null = null;
    quantity = 1;
    qtyOnePrice = 0;
    price = 0;
    bufferGeometry: any;

    partToken: string | null = null;
    orderToken: string | null = null;

    tourModalVisible = false;
    comingSoonModalHeader = '';
    comingSoonModalVisible = false;
    uploadSolidModelModelVisible = false;
    emailSignUpSource = '';
    userSignedUpForEmailNotifications = false;

    orderData: QuickTurnSalesOrder | null = null;

    salesOrders: QuickTurnSalesOrder[] = [];

    couponErrors: string | null = null;
    creditCardErrors: string[] = [];
    isPortalEnabled = true;

    constructor() {
        makeAutoObservable(this);

        const authToken = getAuthToken();

        const getPartToken = localStorage.getItem('part_token');
        const getOrderToken = localStorage.getItem('order_token');
        const getUserSignedUpForEmailNotifications = localStorage.getItem('quickturn_email_notifications_accepted');

        if (!authToken) {
            // not logged in; never trust local QT tokens
            localStorage.removeItem('order_token');
            localStorage.removeItem('part_token');
            this.orderToken = null;
            this.partToken = null;
            this.orderData = null;
        } else {
            if (getPartToken) this.setPartToken(getPartToken);

            if (getOrderToken) {
                this.setOrderToken(getOrderToken);
                this.getSalesOrderById(getOrderToken, true);
            }
        }

        if (getUserSignedUpForEmailNotifications) {
            this.userSignedUpForEmailNotifications = true;
        }
    }

    setCouponError(error: string | null) {
        this.couponErrors = error;

        if (error) {
            setTimeout(() => (this.couponErrors = null), 5000);
        }
    }

    setCreditCardError(errors: string[]) {
        this.creditCardErrors = errors;
    }

    setPartToken(token: string | null) {
        this.partToken = token;
    }

    setOrderToken(token: string | null) {
        this.orderToken = token;
    }

    setTourModalVisible(value: boolean) {
        this.tourModalVisible = value;

        if (value === false) {
            const now = new Date();
            localStorage.setItem('tour_modal_dismissed', JSON.stringify(now));
        }
    }

    setUserSignedUpForEmailNotifications() {
        const now = new Date();

        localStorage.setItem('quickturn_email_notifications_accepted', JSON.stringify(now));

        this.userSignedUpForEmailNotifications = true;
    }

    setComingSoonModalHeader(value: string) {
        this.comingSoonModalHeader = value;
    }

    setEmailSignUpSource(value: string) {
        this.emailSignUpSource = value;
    }

    setComingSoonModalVisible(value: boolean) {
        if (value === false) {
            this.emailSignUpSource = '';
        }

        this.comingSoonModalVisible = value;
    }

    setUploadSolidModelModelVisible(value: boolean) {
        this.uploadSolidModelModelVisible = value;
    }

    setMaterials(value: QuickTurnPartMaterial[]) {
        this.materials = value;
    }

    setName(value: string) {
        this.name = value;
    }

    setOuterDiameter(value: number) {
        this.outerDiameter = Number(value?.toFixed(3));
    }

    setOverallLength(value: number) {
        this.overallLength = Number(value?.toFixed(3));
    }

    setHasThruHole(value: boolean) {
        this.hasThruHole = value;
    }

    setInnerDiameter(value: number) {
        this.innerDiameter = Number(value?.toFixed(3));
    }

    setHasFrontCountersink(value: boolean) {
        this.hasFrontCountersink = value;
    }

    setFrontCountersinkAngle(value: number) {
        this.frontCountersinkAngle = Number(value?.toFixed(0));
    }

    setFrontCountersinkDepth(value: number) {
        this.frontCountersinkDepth = Number(value?.toFixed(3));
    }

    // WIP
    setFrontCountersinkMajorDiameter(value: number) {
        const depth =
            Math.tan(degreesToRadians(90 - this.frontCountersinkAngle / 2)) * (value / 2 - this.innerDiameter / 2);

        this.frontCountersinkDepth = Number(depth.toFixed(3));
    }

    setHasFlange(value: boolean) {
        this.hasFlange = value;
    }

    setFlangeDiameter(value: number) {
        this.flangeDiameter = Number(value?.toFixed(3));
    }

    setFlangeThickness(value: number) {
        this.flangeThickness = Number(value?.toFixed(3));
    }

    setHasFrontCounterbore(value: boolean) {
        this.hasFrontCounterbore = value;
    }

    setFrontCounterboreDiameter(value: number) {
        this.frontCounterboreDiameter = Number(value?.toFixed(3));
    }

    setFrontCounterboreDepth(value: number) {
        this.frontCounterboreDepth = Number(value?.toFixed(3));
    }

    setChamferWidth(value: number) {
        this.chamferWidth = Number(value?.toFixed(3));
    }

    setMaterial(value: string) {
        this.material = value;
    }

    setQuantity(value: number) {
        this.quantity = value;
    }

    setQtyOnePrice(value: number) {
        this.qtyOnePrice = value;
    }

    setPrice(value: number) {
        this.price = value;
    }

    setBufferGeometry(value: any) {
        this.bufferGeometry = value;
    }

    setSalesOrders(value: any) {
        this.salesOrders = value;
    }

    // get frontCounterSinkDepth() {
    //     return this.frontCounterSinkDepth;
    // }

    get frontCountersinkMajorDiameter() {
        const tangent = Math.tan(degreesToRadians(this.frontCountersinkAngle / 2)) * this.frontCountersinkDepth;

        return tangent * 2 + this.innerDiameter;
    }

    init() {
        this.getMaterials();
    }

    async getMaterials() {
        try {
            const data = await get(`/quickturn/core/materials`);

            this.setMaterials(data);
        } catch (err) {
            message.error(`Error: ${err}`);

            return err;
        }
    }

    async signUpForEmailNotifications(values: IEmailSignUp) {
        try {
            const payload = {
                email_address: values?.email_address,
                source: values?.source,
            };

            await post(`/quickturn/core/sign-up-for-email/`, payload);

            this.setUserSignedUpForEmailNotifications();
        } catch (err) {
            message.error(`Error: ${err}`);

            return err;
        }
    }

    async startNewPart(values?: any) {
        try {
            this.loading = true;

            const data = await post(`/quickturn/api/parts/`, values);

            if (data?.id) {
                localStorage.setItem('part_token', data['id']);

                this.partToken = data['id'];

                this.getPartByID(data['id']);
            }

            this.loading = false;
        } catch (err) {
            this.loading = false;

            message.error(`Error: ${err}`);
            return err;
        }
    }

    async getPartByID(id: string) {
        try {
            this.loading = true;

            const isValidUUID = checkIsValidUUID(id);

            if (isValidUUID) {
                const data = await get(`/quickturn/api/parts/${id}/`);

                this.setName(data?.name);
                this.setQuantity(data?.quantity);
                this.setMaterial(data?.material?.id);
                this.setOuterDiameter(data?.outerDiameter);
                this.setOverallLength(data?.overallLength);
                this.setHasThruHole(data?.hasThruHole);
                this.setInnerDiameter(data?.innerDiameter);
                this.setHasFrontCountersink(data?.hasFrontCountersink);
                this.setFrontCountersinkAngle(data?.frontCountersinkAngle);
                this.setFrontCountersinkDepth(data?.frontCountersinkDepth);
                this.setHasFlange(data?.hasFlange);
                this.setFlangeDiameter(data?.flangeDiameter);
                this.setFlangeThickness(data?.flangeThickness);
                this.setHasFrontCounterbore(data?.hasFrontCounterbore);
                this.setFrontCounterboreDiameter(data?.frontCounterboreDiameter);
                this.setFrontCounterboreDepth(data?.frontCounterboreDepth);
                this.setChamferWidth(data?.chamferWidth);
                this.setQtyOnePrice(data?.price);
                this.setPrice(data?.volume_price_single);

                this.setBufferGeometry(JSON.stringify(data?.get_buffer_geometry));
            } else {
                localStorage.removeItem('part_token');
                this.startNewPart();
            }

            this.loading = false;
        } catch (err) {
            localStorage.removeItem('part_token');
            this.startNewPart();

            this.loading = false;

            return;
        }
    }

    /**
     * This function sends updated part specifications to the API endpoint,
     * and returns the generated buffer geometry but does not save the changes to the database.
     **/
    async previewPartById(id: string) {
        try {
            this.loading = true;

            const payload = {
                name: this.name,
                quantity: this.quantity,
                material: this.material,
                outerDiameter: this.outerDiameter,
                overallLength: this.overallLength,
                hasThruHole: this.hasThruHole,
                innerDiameter: this.innerDiameter,
                hasFrontCountersink: this.hasFrontCountersink,
                frontCountersinkAngle: this.frontCountersinkAngle,
                frontCountersinkDepth: this.frontCountersinkDepth,
                hasFlange: this.hasFlange,
                flangeDiameter: this.flangeDiameter,
                flangeThickness: this.flangeThickness,
                hasFrontCounterbore: this.hasFrontCounterbore,
                frontCounterboreDiameter: this.frontCounterboreDiameter,
                frontCounterboreDepth: this.frontCounterboreDepth,
                chamferWidth: this.chamferWidth,
            };

            const data = await post(`/quickturn/api/parts/${id}/preview/`, payload);

            this.setQtyOnePrice(data?.price);
            this.setPrice(data?.volume_price_single);

            this.setBufferGeometry(JSON.stringify(data?.get_buffer_geometry));

            this.loading = false;
        } catch (err) {
            this.loading = false;
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async updatePartById(id: string | null, quantity?: number, generateThumbnail?: boolean) {
        try {
            this.loading = true;

            const payload = {
                name: this.name,
                quantity: quantity ? quantity : this.quantity,
                material: this.material,
                outerDiameter: this.outerDiameter,
                overallLength: this.overallLength,
                hasThruHole: this.hasThruHole,
                innerDiameter: this.innerDiameter,
                hasFrontCountersink: this.hasFrontCountersink,
                frontCountersinkAngle: this.frontCountersinkAngle,
                frontCountersinkDepth: this.frontCountersinkDepth,
                hasFlange: this.hasFlange,
                flangeDiameter: this.flangeDiameter,
                flangeThickness: this.flangeThickness,
                hasFrontCounterbore: this.hasFrontCounterbore,
                frontCounterboreDiameter: this.frontCounterboreDiameter,
                frontCounterboreDepth: this.frontCounterboreDepth,
                chamferWidth: this.chamferWidth,
                generateThumbnail: generateThumbnail === undefined ? false : generateThumbnail,
            };

            const data = await patch(`/quickturn/api/parts/${id}/`, payload);

            this.setQtyOnePrice(data?.price);
            this.setPrice(data?.volume_price_single);

            this.setBufferGeometry(JSON.stringify(data?.get_buffer_geometry));

            this.loading = false;
        } catch (err) {
            this.loading = false;
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async updatePartQuantity(id: string, quantity: number) {
        try {
            this.loading = true;

            const payload = {
                quantity: quantity,
            };

            const data = await patch(`/quickturn/api/parts/${id}/`, payload);

            this.setQtyOnePrice(data?.price);
            this.setPrice(data?.price);

            this.loading = false;
        } catch (err) {
            this.loading = false;
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async updatePartName(name: string) {
        try {
            this.loading = true;

            const payload = {
                name: name,
            };
            const data = await patch(`/quickturn/api/parts/${this.partToken}/`, payload);

            this.setName(data?.name);

            this.loading = false;
        } catch (err) {
            this.loading = false;
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async getSalesOrders() {
        try {
            const data = await get(`/quickturn/api/sales-order/history`);

            this.setSalesOrders(data);
        } catch (err) {
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async getCart() {
        try {
            const data = await get(`/quickturn/api/sales-order/`, {
                params: {
                    status: 'Draft',
                },
            });

            if (data?.length) {
                this.orderData = data[0];

                localStorage.setItem('order_token', data[0].id);
                this.setOrderToken(data[0].id);
            }
        } catch (err) {
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async getCompletedSalesOrderById(id: string | null) {
        try {
            const data = await get(`/quickturn/api/sales-order/${id}/`, {
                params: {
                    status: 'Order Received',
                },
            });

            return data;
        } catch (err) {
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async getSalesOrderById(id: string | null, createNewOrderOn404?: boolean) {
        const response = axios
            .get(`${API_ROOT}/quickturn/api/sales-order/${id}/`, {
                headers: getHeader(),
            })
            .then(response => {
                this.orderData = response.data;

                return response.data;
            })
            .catch(async err => {
                if (err.response.status == 404) {
                    localStorage.removeItem('order_token');
                    this.orderData = null;
                    this.setOrderToken(null);

                    if (createNewOrderOn404) {
                        const data = await this.createSalesOrder();
                        return data;
                    }

                    return true;
                }
                message.error(`Error: ${err}`);
                return err;
            });

        return response;
    }

    async createSalesOrder() {
        try {
            const payload = {};

            const data = await post(`/quickturn/api/sales-order/`, payload);

            localStorage.setItem('order_token', data['id']);

            this.setOrderToken(data['id']);

            return data;
        } catch (err) {
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async addItemToSalesOrder() {
        try {
            if (!this.orderToken) {
                await this.createSalesOrder();
            }

            const payload = {
                name: this.name,
                order: this.orderToken,
                part: this.partToken,
                quantity: this.quantity,
                unit_price: this.price,
            };

            const data = await post(`/quickturn/api/sales-order/${this.orderToken}/add-item/`, payload);

            this.orderData = data;

            return data;
        } catch (err) {
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async addPartToCart() {
        await this.addItemToSalesOrder();

        localStorage.removeItem('part_token');

        await this.startNewPart();
    }

    async removeItemFromOrder(id: string) {
        try {
            const data = await remove(`/quickturn/api/sales-order/${this.orderToken}/remove-item/${id}/`);

            this.orderData = data;
        } catch (err) {
            return err;
        }
    }

    async updateSalesOrder(token: string | null, payload: any) {
        try {
            const response = await patch(`/quickturn/api/sales-order/${token}/`, payload);

            if (response?.status === 'Draft') {
                this.orderData = response;
            }
        } catch (err) {
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async updateSalesOrderItem(payload: { line_item: string; quantity?: number }) {
        try {
            const data = await patch(`/quickturn/api/sales-order/${this.orderToken}/update-item/`, payload);

            this.orderData = data;
        } catch (err) {
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async applyCouponCode(coupon_code: string) {
        try {
            const response = await axios.post(
                `${API_ROOT}/quickturn/api/sales-order/${this.orderToken}/apply-coupon/`,
                {
                    coupon_code: coupon_code,
                },
                { headers: getHeader() }
            );

            this.orderData = response.data;

            return response.data;
        } catch (err) {
            if (axios.isAxiosError(err)) {
                this.setCouponError(err.response?.data?.error);
            } else {
                message.error(`Error: ${err}`);
                return err;
            }
        }
    }

    async removeCoupon() {
        try {
            const response = await axios.delete(
                `${API_ROOT}/quickturn/api/sales-order/${this.orderToken}/remove-coupon/`,
                {
                    headers: getHeader(),
                }
            );

            this.orderData = response.data;

            return response.data;
        } catch (err) {
            if (axios.isAxiosError(err)) {
                this.setCouponError(err.response?.data?.error);
            } else {
                message.error(`Error: ${err}`);
                return err;
            }
        }
    }

    async sendToReview(payload: any) {
        await post(`/quickturn/api/sales-order/${this.orderToken}/send-to-review/`, payload);

        localStorage.removeItem('order_token');
        localStorage.removeItem('part_token');

        this.setPartToken(null);
        this.setOrderToken(null);

        this.orderData = null;
    }

    async getCompanyParams(name: string) {
        const data: ICompanyParams[] = await get(`/quickturn/core/company-params/`, { params: { name: name } });

        if (name === 'PortalEnabledParam') {
            const getPortalEnabledParamData = data?.find(item => item.name === name);

            if (getPortalEnabledParamData) {
                this.isPortalEnabled = getPortalEnabledParamData?.value?.enabled ? true : false;

                return this.isPortalEnabled;
            }

            this.isPortalEnabled = true;
            return this.isPortalEnabled;
        }

        return data;
    }
}

export default new QuickTurnPartStore();
